import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, throwError, of } from 'rxjs';
import { map, catchError, tap, switchMap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { AuthRequest, AuthResponse, TokenInfo, EmailSendRequest, EmailSendResponse } from './models';

@Injectable({
    providedIn: 'root'
})
export class EmailRestService {
    private tokenInfo: TokenInfo | null = null;
    private readonly TOKEN_VALIDITY_BUFFER = 60000; // 60 secondi di buffer prima della scadenza

    constructor(private http: HttpClient) { }

    /**
     * Autentica il service con credenziali fisse e ottiene il token JWT
     */
    private authenticate(): Observable<TokenInfo> {
        const authRequest: AuthRequest = {
            username: environment.mailService.username,
            password: environment.mailService.password
        };

        const url = `${environment.mailService.baseUrl}${environment.mailService.endpoints.auth}`;

        return this.http.post<AuthResponse>(url, authRequest).pipe(
            map(response => {
                // JWT valido per 1 ora (3600 secondi) come da documentazione
                const expiresIn = 3600;
                const expirationTime = Date.now() + (expiresIn * 1000);

                const tokenInfo: TokenInfo = {
                    token: response.token,
                    expirationTime: expirationTime
                };

                this.tokenInfo = tokenInfo;
                return tokenInfo;
            }),
            catchError(error => {
                console.error('Errore durante l\'autenticazione al servizio email:', error);
                return throwError(() => error);
            })
        );
    }

    /**
     * Verifica se il token corrente è ancora valido
     */
    private isTokenValid(): boolean {
        if (!this.tokenInfo) {
            return false;
        }

        // Controlla se il token è scaduto (con buffer di sicurezza)
        const now = Date.now();
        return now < (this.tokenInfo.expirationTime - this.TOKEN_VALIDITY_BUFFER);
    }

    /**
     * Ottiene un token valido, autenticandosi se necessario
     */
    private getValidToken(): Observable<string> {
        if (this.isTokenValid() && this.tokenInfo) {
            return of(this.tokenInfo.token);
        }

        // Token non valido o assente, autentica
        return this.authenticate().pipe(
            map(tokenInfo => tokenInfo.token)
        );
    }

    /**
     * Invia un'email utilizzando il servizio REST
     */
    public sendEmail(emailData: EmailSendRequest): Observable<EmailSendResponse> {
        return this.getValidToken().pipe(
            switchMap(token => {
                const url = `${environment.mailService.baseUrl}${environment.mailService.endpoints.send}`;
                
                const headers = new HttpHeaders({
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                });

                return this.http.post<EmailSendResponse>(url, emailData, { headers }).pipe(
                    tap(response => {
                        console.log('Email inviata con successo:', response);
                    }),
                    catchError(error => {
                        console.error('Errore durante l\'invio dell\'email:', error);
                        
                        // Se errore di autenticazione (401), invalida il token e riprova una volta
                        if (error.status === 401) {
                            this.tokenInfo = null;
                            console.log('Token invalidato, nuovo tentativo...');
                            
                            // Riprova una volta sola
                            return this.getValidToken().pipe(
                                switchMap(newToken => {
                                    const newHeaders = new HttpHeaders({
                                        'Content-Type': 'application/json',
                                        'Authorization': `Bearer ${newToken}`
                                    });
                                    return this.http.post<EmailSendResponse>(url, emailData, { headers: newHeaders });
                                })
                            );
                        }
                        
                        return throwError(() => error);
                    })
                );
            })
        );
    }

    /**
     * Invalida manualmente il token (utile per logout o reset)
     */
    public invalidateToken(): void {
        this.tokenInfo = null;
    }
}
